import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Input,
  InputNumber,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowRightOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  LinkOutlined,
  LoadingOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import AIService from "../services/AIService";
import { SEEDANCE_IMAGE_TO_VIDEO_MODEL } from "../services/modelCatalog";
import PromptEditorModal, { PromptEditorButton } from "./PromptEditorModal";
import { getSectionOverride, setSectionOverride } from "../services/sectionOverridesStore";
import { uploadGeneratedImage } from "../services/knowledgeStore";

const { Text, Title } = Typography;

const ATLAS_CONSOLE = "https://console.atlascloud.ai/api/v1";

const VIDEO_DURATION_OPTIONS = [
  { value: -1, label: "Auto" },
  { value: 4, label: "4 s" },
  { value: 5, label: "5 s" },
  { value: 6, label: "6 s" },
  { value: 7, label: "7 s" },
  { value: 8, label: "8 s" },
  { value: 10, label: "10 s" },
  { value: 12, label: "12 s" },
  { value: 15, label: "15 s" },
];

const VIDEO_RATIO_OPTIONS = [
  { value: "16:9", label: "16:9" },
  { value: "9:16", label: "9:16" },
  { value: "1:1", label: "1:1" },
  { value: "4:3", label: "4:3" },
  { value: "21:9", label: "21:9" },
  { value: "adaptive", label: "Adaptive" },
];

const VIDEO_RESOLUTION_OPTIONS = [
  { value: "720p", label: "720p" },
  { value: "1080p", label: "1080p" },
  { value: "1080p-SR", label: "1080p SR" },
  { value: "1440p-SR", label: "1440p SR" },
];

const IMAGE_RATIO_OPTIONS = [
  { value: "1:1", label: "1:1" },
  { value: "9:16", label: "9:16" },
  { value: "16:9", label: "16:9" },
];

// Small thumbnail that shows a selected ring and an optional action button
const ImageThumb = ({ url, label, selected, onClick, action, actionLabel }) => (
  <Tooltip title={label}>
    <div style={{ position: "relative", flexShrink: 0 }}>
      <div
        onClick={onClick}
        style={{
          width: 72, height: 72, borderRadius: 8, overflow: "hidden",
          cursor: onClick ? "pointer" : "default",
          border: selected ? "2px solid #1677ff" : "2px solid transparent",
          outline: selected ? "2px solid #1677ff44" : "none",
          position: "relative",
        }}
      >
        <img src={url} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        {selected && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(22,119,255,0.28)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircleOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>
        )}
      </div>
      {action && (
        <Button
          size="small"
          type="primary"
          icon={<ArrowRightOutlined />}
          onClick={action}
          style={{ position: "absolute", bottom: -10, left: "50%", transform: "translateX(-50%)", fontSize: 10, padding: "0 4px", height: 18, lineHeight: "16px" }}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  </Tooltip>
);

// Generate a short, sluggified filename from a motion prompt — first 2-3 meaningful words.
// e.g. "slow cinematic push-in on a wet hand and trimmer" → "wethandtrimmer"
const slugifyPrompt = (prompt, maxWords = 3) => {
  const STOP = new Set([
    "the","a","an","and","or","of","on","in","to","with","for","is","it","at","by","up","as",
    "his","her","their","its","this","that","these","those","be","very","gently","slow","slowly",
    "cinematic","camera","shot","video","scene","motion","push","push-in","pull","zoom","in","out",
  ]);
  const words = String(prompt || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .split(/\s+/)
    .filter((w) => w && !STOP.has(w) && w.length > 2);
  const picked = words.slice(0, maxWords).join("");
  return picked || "clip";
};

const triggerDownload = async (url, filename) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
  } catch {
    // Fallback: just open in new tab (CORS-blocked CDNs)
    window.open(url, "_blank", "noopener");
  }
};

const KLING_DURATION_OPTIONS = [
  { value: 3, label: "3 s" }, { value: 4, label: "4 s" }, { value: 5, label: "5 s" },
  { value: 6, label: "6 s" }, { value: 7, label: "7 s" }, { value: 8, label: "8 s" },
  { value: 9, label: "9 s" }, { value: 10, label: "10 s" },
];

const KLING_MODEL_OPTIONS = [
  { value: "kling-v3-omni", label: "Kling 3.0 Omni" },
  { value: "kling-video-o1", label: "Kling Video O1" },
];

const KLING_MODE_OPTIONS = [
  { value: "std", label: "Standard (720p)" },
  { value: "pro", label: "Pro (1080p)" },
  { value: "4k", label: "4K" },
];

const VideoStudioSection = ({
  atlascloudKey = "",
  onAtlascloudKeyChange,
  klingKey = "",
  onKlingKeyChange,
  knowledgeDb = [],
  workspaceId = null,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  // ── Provider ──────────────────────────────────────────────────────────────
  const [videoProvider, setVideoProvider] = useState("seedance"); // "seedance" | "kling"


  // ── Kling-specific settings ───────────────────────────────────────────────
  const [klingModel, setKlingModel] = useState("kling-v3-omni");
  const [klingMode, setKlingMode] = useState("pro");
  const [klingSound, setKlingSound] = useState("off");
  const [klingDuration, setKlingDuration] = useState(5);

  // ── Knowledge images ─────────────────────────────────────────────────────
  const productImageOptions = React.useMemo(() => {
    const out = [];
    for (const entry of knowledgeDb || []) {
      for (const img of entry?.images || []) {
        const url = img?.url || img?.mediaUrl || img?.signedUrl || "";
        if (url) out.push({ url, label: img?.name || entry?.name || "Image", entryName: entry?.name || "" });
      }
    }
    return out;
  }, [knowledgeDb]);

  // ── Image Generator state ─────────────────────────────────────────────────
  const [imgPrompt, setImgPrompt] = useState("");
  const [imgRatio, setImgRatio] = useState("9:16");
  const [imgRefImages, setImgRefImages] = useState([]); // product library refs for prompt context
  const [imgRunning, setImgRunning] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]); // array of data URL strings
  const [imgShareUrls, setImgShareUrls] = useState({}); // dataUrl → public URL (uploaded)
  const [imgUploadingIdx, setImgUploadingIdx] = useState(null);
  const imgAbortRef = useRef(null);

  // ── Prompt editor state ───────────────────────────────────────────────────
  const [imgPromptEditorOpen, setImgPromptEditorOpen] = useState(false);
  const [imgPromptOverrides, setImgPromptOverridesState] = useState(() => getSectionOverride("videoImage"));
  const [videoPromptEditorOpen, setVideoPromptEditorOpen] = useState(false);
  const [videoPromptOverrides, setVideoPromptOverridesState] = useState(() => getSectionOverride("videoMotion"));

  const setImgPromptOverrides = (v) => { setImgPromptOverridesState(v); setSectionOverride("videoImage", v); };
  const setVideoPromptOverrides = (v) => { setVideoPromptOverridesState(v); setSectionOverride("videoMotion", v); };

  const buildImgFullPrompt = () => {
    const selectedRefs = imgRefImages.length > 0
      ? productImageOptions.filter((o) => imgRefImages.includes(o.url))
      : [];
    const refLabels = selectedRefs.map((o) => o.label).filter(Boolean).join(", ");
    const refInstruction = selectedRefs.length > 0
      ? `IMPORTANT: The attached reference image(s) show the EXACT product${selectedRefs.length > 1 ? "s" : ""} that must appear in the generated image. Match the product's exact shape, color, materials, label, packaging, and proportions from the reference. Do not redesign or substitute the product. Reference labels: ${refLabels}.`
      : "";
    return [
      imgPrompt.trim(),
      refInstruction,
      "High quality, photorealistic, studio lighting.",
    ].filter(Boolean).join("\n\n");
  };

  const toggleImgRef = (url) =>
    setImgRefImages((prev) => prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]);

  // Upload a generated image to Supabase storage and copy the public URL.
  // Reuses the same URL on subsequent clicks instead of re-uploading.
  const handleShareImageUrl = async (dataUrl, idx) => {
    if (imgShareUrls[dataUrl]) {
      try {
        await navigator.clipboard.writeText(imgShareUrls[dataUrl]);
        messageApi.success("URL copied to clipboard.");
      } catch {
        messageApi.error("Could not copy. URL: " + imgShareUrls[dataUrl]);
      }
      return;
    }
    if (!workspaceId) { messageApi.error("Select a workspace first."); return; }
    setImgUploadingIdx(idx);
    try {
      // Convert data URL → Blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const id = `gen-${Date.now()}-${idx}`;
      const slug = slugifyPrompt(imgPrompt) || "image";
      const filename = `${slug}-${idx + 1}.jpg`;
      const result = await uploadGeneratedImage({
        id,
        name: filename,
        blob,
        mimeType: blob.type || "image/jpeg",
      });
      const url = result?.url || "";
      if (!url) throw new Error("No URL returned from upload");
      setImgShareUrls((prev) => ({ ...prev, [dataUrl]: url }));
      try {
        await navigator.clipboard.writeText(url);
        messageApi.success("Public URL copied to clipboard.");
      } catch {
        messageApi.success("Uploaded. URL: " + url);
      }
    } catch (err) {
      messageApi.error(err?.message || "Upload failed.");
    } finally {
      setImgUploadingIdx(null);
    }
  };

  const handleDownloadImage = (dataUrl, idx) => {
    const slug = slugifyPrompt(imgPrompt) || "image";
    // dataUrl from compressDataUrl is JPEG, so use .jpg extension
    const ext = dataUrl.startsWith("data:image/png") ? "png" : "jpg";
    const filename = `${slug}-${idx + 1}.${ext}`;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Compress a data-URL to JPEG, trying progressively smaller sizes until
  // the base64 payload fits within maxBytes (default ~600 KB base64 ≈ 450 KB binary).
  const compressDataUrl = (dataUrl, maxDim = 1024, maxBytes = 600_000) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const tryCompress = (dim, quality) => {
          const scale = Math.min(1, dim / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.round(img.naturalWidth * scale);
          const h = Math.round(img.naturalHeight * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const result = canvas.toDataURL("image/jpeg", quality);
          return result;
        };

        // Try progressively until under maxBytes
        const passes = [
          [maxDim, 0.85],
          [maxDim, 0.72],
          [768,    0.72],
          [640,    0.65],
          [512,    0.60],
        ];
        for (const [dim, q] of passes) {
          const out = tryCompress(dim, q);
          if (out.length <= maxBytes) { resolve(out); return; }
        }
        // Last resort — smallest possible
        resolve(tryCompress(512, 0.55));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });

  const handleGenerateImage = async () => {
    if (!imgPrompt.trim()) { messageApi.error("Enter an image prompt."); return; }
    if (!workspaceId) {
      messageApi.error("No active workspace — select a workspace in the top bar first.");
      return;
    }
    const ctrl = new AbortController();
    imgAbortRef.current = ctrl;
    setImgRunning(true);
    try {
      const aiService = new AIService({ workspaceId });

      const fullPrompt = imgPromptOverrides.main || buildImgFullPrompt();

      // Pass actual reference image URLs to the model (not just labels in the prompt).
      // The selected product images condition the generation when present.
      const referenceImageUrls = imgRefImages.length > 0
        ? productImageOptions
            .filter((o) => imgRefImages.includes(o.url))
            .map((o) => o.url)
            .filter(Boolean)
        : [];

      const raw = await aiService.generateImageFromPrompt({
        prompt: fullPrompt,
        aspectRatio: imgRatio,
        referenceImages: referenceImageUrls,
        signal: ctrl.signal,
      });
      const dataUrl = await compressDataUrl(raw);
      setGeneratedImages((prev) => [dataUrl, ...prev].slice(0, 12));
    } catch (err) {
      if (err?.name !== "AbortError") messageApi.error(err?.message || "Image generation failed.");
    } finally {
      setImgRunning(false);
      imgAbortRef.current = null;
    }
  };

  // ── First-frame & video gen state ─────────────────────────────────────────
  const [firstFrameImage, setFirstFrameImage] = useState(null); // URL or data URL
  const [firstFrameLabel, setFirstFrameLabel] = useState("");

  const setFirstFrame = (url, label = "Image") => {
    setFirstFrameImage(url);
    setFirstFrameLabel(label);
    messageApi.success(`"${label}" set as first frame.`);
  };

  const [videoConcept, setVideoConcept] = useState("");
  const [videoClipCount, setVideoClipCount] = useState(1);
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoRatio, setVideoRatio] = useState("adaptive");
  const [videoResolution, setVideoResolution] = useState("720p");
  const [videoGenerateAudio, setVideoGenerateAudio] = useState(false);
  const [videoRunning, setVideoRunning] = useState(false);
  const [videoClips, setVideoClips] = useState([]);
  const [videoProgress, setVideoProgress] = useState({ done: 0, total: 0 });
  const videoAbortRef = useRef(null);

  // ── Character continuity ─────────────────────────────────────────────────
  const [charRefFile, setCharRefFile] = useState(null);
  const [charRefPreview, setCharRefPreview] = useState("");
  const [charAssetId, setCharAssetId] = useState("");
  const [charAssetStatus, setCharAssetStatus] = useState("idle");
  const charAtlasId = useRef("");

  const handleCharRefSelect = (file) => {
    if (!file) return false;
    setCharRefFile(file);
    setCharAssetStatus("idle");
    charAtlasId.current = "";
    setCharAssetId("");
    const reader = new FileReader();
    reader.onload = (e) => setCharRefPreview(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleRegisterCharAsset = async () => {
    if (!charRefFile || !atlascloudKey) {
      messageApi.error("Upload a portrait and enter your AtlasCloud key first.");
      return;
    }
    setCharAssetStatus("uploading");
    try {
      const toBase64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      const dataUrl = await toBase64(charRefFile);
      const cRes = await fetch(`${ATLAS_CONSOLE}/sd/assets`, {
        method: "POST",
        headers: { Authorization: `Bearer ${atlascloudKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ url: dataUrl, name: charRefFile.name || "Portrait", asset_type: "Image" }),
      });
      if (!cRes.ok) throw new Error(`Asset create failed (${cRes.status})`);
      const cj = await cRes.json();
      const dbId = cj?.data?.id;
      charAtlasId.current = cj?.data?.atlas_asset_id || "";
      setCharAssetId(charAtlasId.current);
      setCharAssetStatus("processing");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const pr = await fetch(`${ATLAS_CONSOLE}/sd/assets/${dbId}`, { headers: { Authorization: `Bearer ${atlascloudKey}` } });
        if (!pr.ok) continue;
        const pj = await pr.json();
        const st = String(pj?.data?.status || "").toLowerCase();
        if (st === "active") { charAtlasId.current = pj?.data?.atlas_asset_id || charAtlasId.current; setCharAssetId(charAtlasId.current); setCharAssetStatus("active"); messageApi.success("Character locked."); return; }
        if (st === "failed") throw new Error(pj?.data?.error_message || "Processing failed");
      }
      throw new Error("Timed out.");
    } catch (err) { setCharAssetStatus("failed"); messageApi.error(err?.message || "Character asset failed."); }
  };

  // ── Generate video ────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!firstFrameImage && videoProvider !== "kling") {
      messageApi.error("Set a first frame image above first."); return;
    }
    if (videoProvider === "seedance" && !atlascloudKey) {
      messageApi.error("Enter your AtlasCloud API key first."); return;
    }
    if (videoProvider === "kling" && !klingKey) {
      messageApi.error("Enter your Kling API key first."); return;
    }
    const controller = new AbortController();
    videoAbortRef.current = controller;
    setVideoRunning(true);
    setVideoClips([]);
    setVideoProgress({ done: 0, total: videoClipCount });

    const motionPrompt = videoPromptOverrides.main || videoConcept.trim() || "The scene comes alive with gentle motion and cinematic lighting";

    // ── Kling path ─────────────────────────────────────────────────────────
    if (videoProvider === "kling") {
      const aiService = new AIService({ kling: klingKey, workspaceId });
      for (let i = 0; i < videoClipCount; i++) {
        if (controller.signal.aborted) break;
        setVideoClips((prev) => [...prev, { index: i, status: "generating", prompt: motionPrompt }]);
        try {
          const result = await aiService.generateVideoFromKling({
            prompt: motionPrompt,
            modelId: klingModel,
            aspectRatio: videoRatio === "adaptive" ? "16:9" : videoRatio,
            durationSeconds: klingDuration,
            mode: klingMode,
            sound: klingSound,
            firstFrameImage: firstFrameImage || null,
            onStatusUpdate: (remoteStatus, elapsedSec) => {
              setVideoClips((prev) =>
                prev.map((c) => c.index === i ? { ...c, remoteStatus, elapsedSec } : c),
              );
            },
            signal: controller.signal,
          });
          setVideoClips((prev) =>
            prev.map((c) => c.index === i ? { ...c, status: "done", videoUrl: result.videoUrl } : c),
          );
        } catch (err) {
          if (err?.name === "AbortError") {
            setVideoClips((prev) => prev.map((c) => c.status === "generating" ? { ...c, status: "cancelled", error: "Cancelled" } : c));
            break;
          }
          setVideoClips((prev) => prev.map((c) => c.index === i ? { ...c, status: "error", error: err?.message || "Failed" } : c));
        }
        setVideoProgress({ done: i + 1, total: videoClipCount });
      }
      setVideoRunning(false);
      videoAbortRef.current = null;
      return;
    }

    // ── Seedance path ──────────────────────────────────────────────────────
    const assetRefs = charAtlasId.current ? [`asset://${charAtlasId.current}`] : [];
    const aiService = new AIService({ atlascloud: atlascloudKey, workspaceId });

    // Compress the first-frame image if it's a data URL — AtlasCloud has a tight
    // request body size limit. Remote URLs pass through untouched.
    const seedanceFirstFrame = firstFrameImage?.startsWith("data:")
      ? await compressDataUrl(firstFrameImage, 1024, 700_000)
      : firstFrameImage;

    for (let i = 0; i < videoClipCount; i++) {
      if (controller.signal.aborted) break;
      setVideoClips((prev) => [...prev, { index: i, status: "generating", prompt: motionPrompt }]);
      try {
        const result = await aiService.generateVideoFromSeedance({
          prompt: motionPrompt,
          modelId: SEEDANCE_IMAGE_TO_VIDEO_MODEL,
          aspectRatio: videoRatio,
          resolution: videoResolution,
          durationSeconds: videoDuration,
          referenceImages: assetRefs,
          generateAudio: videoGenerateAudio,
          firstFrameImage: seedanceFirstFrame,
          onStatusUpdate: (remoteStatus, elapsedSec) => {
            setVideoClips((prev) =>
              prev.map((c) => c.index === i ? { ...c, remoteStatus, elapsedSec } : c),
            );
          },
          signal: controller.signal,
        });
        setVideoClips((prev) =>
          prev.map((c) => c.index === i ? { ...c, status: "done", videoUrl: result.videoUrl } : c),
        );
      } catch (err) {
        if (err?.name === "AbortError") {
          setVideoClips((prev) => prev.map((c) => c.status === "generating" ? { ...c, status: "cancelled", error: "Cancelled" } : c));
          break;
        }
        setVideoClips((prev) => prev.map((c) => c.index === i ? { ...c, status: "error", error: err?.message || "Failed" } : c));
      }
      setVideoProgress({ done: i + 1, total: videoClipCount });
    }
    setVideoRunning(false);
    videoAbortRef.current = null;
  };

  const handleCancel = () => { videoAbortRef.current?.abort(); setVideoRunning(false); };
  const handleReset = () => { setVideoClips([]); setVideoProgress({ done: 0, total: 0 }); };

  const keyIsSet = Boolean(atlascloudKey);
  const klingKeyIsSet = Boolean(klingKey);

  return (
    <div>
      {contextHolder}
      <Divider orientation="left">
        <Space>
          <VideoCameraOutlined />
          <Text strong>Video Studio</Text>
          <Tag color={videoProvider === "kling" ? "purple" : "blue"}>
            {videoProvider === "kling" ? "Kling 3.0 Omni" : "Seedance 2.0 · Image→Video"}
          </Tag>
        </Space>
      </Divider>

      {/* ── Key status (read-only — configure in Settings) ───────────────── */}
      {(!keyIsSet || !klingKeyIsSet) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12, fontSize: 12 }}
          message={
            <span>
              {!keyIsSet && !klingKeyIsSet
                ? "AtlasCloud and Kling API keys are not set."
                : !keyIsSet
                  ? "AtlasCloud API key is not set."
                  : "Kling API key is not set."}
              {" "}Configure them in <strong>Settings → API Keys</strong>.
            </span>
          }
        />
      )}

      {/* ── STEP 1: AI Image Generator ──────────────────────────────────── */}
      <Card
        className="panel-card"
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <BulbOutlined />
            <Text strong>Step 1 — Generate Image</Text>
            <Tag>GPT Image 2</Tag>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Image Prompt</Text>
                <Input.TextArea
                  rows={3}
                  placeholder='e.g. "A man trimming his beard with a sleek black electric trimmer, studio lighting, close up, photorealistic"'
                  value={imgPrompt}
                  onChange={(e) => setImgPrompt(e.target.value)}
                  maxLength={600}
                  showCount
                  style={{ marginTop: 6 }}
                />
              </div>

              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Aspect Ratio</Text>
                <Select value={imgRatio} onChange={setImgRatio} style={{ width: "100%", marginTop: 6 }} options={IMAGE_RATIO_OPTIONS} />
              </div>

              {/* Product library for image gen reference */}
              {productImageOptions.length > 0 && (
                <div>
                  <Space align="center" style={{ marginBottom: 6 }}>
                    <PictureOutlined />
                    <Text style={{ fontSize: 13 }}>Product Library</Text>
                    {imgRefImages.length > 0 && <Tag color="blue">{imgRefImages.length} selected</Tag>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                    Select product images — their labels inform the AI prompt for better product accuracy.
                  </Text>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                    {productImageOptions.slice(0, 20).map((img) => (
                      <ImageThumb
                        key={img.url}
                        url={img.url}
                        label={img.label}
                        selected={imgRefImages.includes(img.url)}
                        onClick={() => toggleImgRef(img.url)}
                      />
                    ))}
                  </div>
                  {imgRefImages.length > 0 && (
                    <Button size="small" onClick={() => setImgRefImages([])}>Clear</Button>
                  )}
                </div>
              )}

              <Space>
                <Button
                  type="primary"
                  icon={<BulbOutlined />}
                  onClick={handleGenerateImage}
                  loading={imgRunning}
                  disabled={!imgPrompt.trim()}
                >
                  Generate Image
                </Button>
                <PromptEditorButton
                  onClick={() => setImgPromptEditorOpen(true)}
                  hasOverride={Boolean(imgPromptOverrides.main)}
                />
                {imgRunning && (
                  <Button size="small" danger onClick={() => { imgAbortRef.current?.abort(); setImgRunning(false); }}>
                    Cancel
                  </Button>
                )}
              </Space>
            </Space>
          </Col>

          {/* Generated images + upload tile */}
          <Col xs={24} lg={12}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignContent: "flex-start" }}>
              {generatedImages.length === 0 && (
                <Text type="secondary" style={{ fontSize: 12, width: "100%", marginBottom: 4 }}>
                  Generated images appear here. Upload or generate an image to use as the first frame.
                </Text>
              )}
              {generatedImages.map((dataUrl, idx) => (
                <div key={idx} style={{ position: "relative" }}>
                  <div
                    style={{
                      width: 120, height: 120, borderRadius: 10, overflow: "hidden",
                      border: firstFrameImage === dataUrl ? "2px solid #1677ff" : "2px solid transparent",
                      cursor: "pointer",
                    }}
                    onClick={() => setFirstFrame(dataUrl, `Generated #${idx + 1}`)}
                  >
                    <img src={dataUrl} alt={`gen-${idx}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <Space size={4} style={{ marginTop: 4, justifyContent: "center", display: "flex", flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      type={firstFrameImage === dataUrl ? "primary" : "default"}
                      icon={<ArrowRightOutlined />}
                      onClick={() => setFirstFrame(dataUrl, `Generated #${idx + 1}`)}
                      style={{ fontSize: 11 }}
                    >
                      {firstFrameImage === dataUrl ? "First Frame ✓" : "→ First Frame"}
                    </Button>
                    <Tooltip title="Download as JPG">
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadImage(dataUrl, idx)}
                      />
                    </Tooltip>
                    <Tooltip title={imgShareUrls[dataUrl] ? "Copy public URL (already uploaded)" : "Upload & copy public URL — share with other AIs"}>
                      <Button
                        size="small"
                        loading={imgUploadingIdx === idx}
                        icon={imgShareUrls[dataUrl] ? <CopyOutlined /> : <LinkOutlined />}
                        onClick={() => handleShareImageUrl(dataUrl, idx)}
                        type={imgShareUrls[dataUrl] ? "primary" : "default"}
                        ghost={Boolean(imgShareUrls[dataUrl])}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
              {/* Upload tile — always last */}
              <div>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const dataUrl = e.target.result;
                      setGeneratedImages((prev) => [dataUrl, ...prev].slice(0, 12));
                      setFirstFrame(dataUrl, file.name || "Uploaded image");
                    };
                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  <Tooltip title="Upload an image to use as first frame">
                    <div
                      style={{
                        width: 120, height: 120, borderRadius: 10,
                        border: "2px dashed #d9d9d9", cursor: "pointer",
                        display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: 6,
                        background: "#fafafa",
                        transition: "border-color 0.2s, background 0.2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1677ff"; e.currentTarget.style.background = "#e6f4ff"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d9d9d9"; e.currentTarget.style.background = "#fafafa"; }}
                    >
                      <PictureOutlined style={{ fontSize: 24, color: "#bfbfbf" }} />
                      <Text type="secondary" style={{ fontSize: 11, textAlign: "center", lineHeight: 1.3 }}>Upload<br/>Image</Text>
                    </div>
                  </Tooltip>
                </Upload>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ── STEP 2: Video Generation ─────────────────────────────────────── */}
      <Card
        className="panel-card"
        size="small"
        title={
          <Space style={{ width: "100%", justifyContent: "space-between", display: "flex", flexWrap: "wrap" }}>
            <Space>
              <VideoCameraOutlined />
              <Text strong>Step 2 — Animate to Video</Text>
              <Tag color={videoProvider === "kling" ? "purple" : "blue"}>
                {videoProvider === "kling" ? `Kling · ${klingModel}` : "Seedance 2.0"}
              </Tag>
              {firstFrameImage && <Tag color="green" icon={<CheckCircleOutlined />}>First frame set — {firstFrameLabel}</Tag>}
            </Space>
            <Segmented
              size="small"
              value={videoProvider}
              onChange={setVideoProvider}
              options={[
                { label: "Seedance", value: "seedance" },
                { label: "Kling", value: "kling" },
              ]}
            />
          </Space>
        }
      >
        {/* Current first frame — always visible, separate from product grid */}
        <div style={{ marginBottom: 16 }}>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 13 }}>First Frame</Text>
            {firstFrameImage
              ? <Tag color="green" icon={<CheckCircleOutlined />}>Set</Tag>
              : <Tag color="orange">Not set</Tag>}
          </Space>

          {firstFrameImage ? (
            <Space align="start" size={12}>
              <div style={{ position: "relative" }}>
                <img
                  src={firstFrameImage}
                  alt="first frame"
                  style={{
                    width: 96, height: 96, objectFit: "cover",
                    borderRadius: 10, border: "2px solid #1677ff",
                  }}
                />
              </div>
              <Space direction="vertical" size={4}>
                <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: firstFrameLabel }}>
                  {firstFrameLabel}
                </Text>
                <Button
                  size="small"
                  danger
                  onClick={() => { setFirstFrameImage(null); setFirstFrameLabel(""); }}
                >
                  Remove
                </Button>
              </Space>
            </Space>
          ) : (
            <Space size={12} align="start">
              <Text type="secondary" style={{ fontSize: 12 }}>
                Generate an image in Step 1, upload below, or pick from the product library.
              </Text>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const dataUrl = e.target.result;
                    setGeneratedImages((prev) => [dataUrl, ...prev].slice(0, 12));
                    setFirstFrame(dataUrl, file.name || "Uploaded image");
                  };
                  reader.readAsDataURL(file);
                  return false;
                }}
              >
                <Tooltip title="Upload an image as first frame">
                  <div
                    style={{
                      width: 72, height: 72, borderRadius: 10,
                      border: "2px dashed #d9d9d9", cursor: "pointer",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: 4,
                      background: "#fafafa",
                      transition: "border-color 0.2s, background 0.2s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1677ff"; e.currentTarget.style.background = "#e6f4ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d9d9d9"; e.currentTarget.style.background = "#fafafa"; }}
                  >
                    <PictureOutlined style={{ fontSize: 20, color: "#bfbfbf" }} />
                    <Text type="secondary" style={{ fontSize: 10, textAlign: "center" }}>Upload</Text>
                  </div>
                </Tooltip>
              </Upload>
            </Space>
          )}
        </div>

        {/* Product library — pick an image directly as first frame */}
        {productImageOptions.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Space align="center" style={{ marginBottom: 6 }}>
              <PictureOutlined />
              <Text style={{ fontSize: 13 }}>Product Library</Text>
            </Space>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {productImageOptions.slice(0, 24).map((img) => (
                <ImageThumb
                  key={img.url}
                  url={img.url}
                  label={img.label}
                  selected={firstFrameImage === img.url}
                  onClick={() => setFirstFrame(img.url, img.label)}
                />
              ))}
            </div>
          </div>
        )}

        <Divider style={{ margin: "8px 0 14px" }} />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Motion Prompt</Text>
                <Input.TextArea
                  rows={4}
                  placeholder={videoProvider === "kling"
                    ? 'Describe the scene, motion, and style. Kling supports detailed prompts — be specific about camera, actions, and mood.'
                    : 'e.g. "slow cinematic push-in, product rotates gently" — or leave blank for auto.'}
                  value={videoConcept}
                  onChange={(e) => setVideoConcept(e.target.value)}
                  style={{ marginTop: 6 }}
                />
                {videoProvider === "seedance" && videoConcept.split(/[.!?]/).filter((s) => s.trim()).length > 2 && (
                  <Text type="warning" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
                    ⚠ Seedance works best with 1–2 short directive sentences. Long or scenario-heavy prompts (e.g. mentions of "podcast", named scenes) often fail content checks.
                  </Text>
                )}
              </div>

              {videoProvider === "kling" ? (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Model</Text>
                      <Select value={klingModel} onChange={setKlingModel} style={{ width: "100%", marginTop: 6 }} options={KLING_MODEL_OPTIONS} />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Quality Mode</Text>
                      <Select value={klingMode} onChange={setKlingMode} style={{ width: "100%", marginTop: 6 }} options={KLING_MODE_OPTIONS} />
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Clips</Text>
                      <InputNumber min={1} max={10} value={videoClipCount} onChange={(v) => setVideoClipCount(v || 1)} style={{ width: "100%", marginTop: 6 }} />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Duration per clip</Text>
                      <Select value={klingDuration} onChange={setKlingDuration} style={{ width: "100%", marginTop: 6 }} options={KLING_DURATION_OPTIONS} />
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Aspect Ratio</Text>
                      <Select value={videoRatio === "adaptive" ? "16:9" : videoRatio} onChange={setVideoRatio} style={{ width: "100%", marginTop: 6 }}
                        options={[{ value: "16:9", label: "16:9" }, { value: "9:16", label: "9:16" }, { value: "1:1", label: "1:1" }]} />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Sound</Text>
                      <Select value={klingSound} onChange={setKlingSound} style={{ width: "100%", marginTop: 6 }}
                        options={[{ value: "off", label: "No sound" }, { value: "on", label: "Generate sound" }]} />
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Clips</Text>
                      <InputNumber min={1} max={10} value={videoClipCount} onChange={(v) => setVideoClipCount(v || 1)} style={{ width: "100%", marginTop: 6 }} />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Duration per clip</Text>
                      <Select value={videoDuration} onChange={setVideoDuration} style={{ width: "100%", marginTop: 6 }} options={VIDEO_DURATION_OPTIONS} />
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Aspect Ratio</Text>
                      <Select value={videoRatio} onChange={setVideoRatio} style={{ width: "100%", marginTop: 6 }} options={VIDEO_RATIO_OPTIONS} />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Resolution</Text>
                      <Select value={videoResolution} onChange={setVideoResolution} style={{ width: "100%", marginTop: 6 }} options={VIDEO_RESOLUTION_OPTIONS} />
                    </Col>
                  </Row>
                  <Space align="center">
                    <Switch checked={videoGenerateAudio} onChange={setVideoGenerateAudio} size="small" />
                    <Text style={{ fontSize: 13 }}>Generate audio (voice, SFX, music)</Text>
                  </Space>
                </>
              )}

              <Divider style={{ margin: "4px 0" }} />

              {/* Character continuity */}
              <div>
                <Space align="center" style={{ marginBottom: 6 }}>
                  <UserOutlined />
                  <Text strong style={{ fontSize: 13 }}>Character Continuity</Text>
                  {charAssetStatus === "active" && <Tag color="green" icon={<CheckCircleOutlined />}>Locked</Tag>}
                  {(charAssetStatus === "processing" || charAssetStatus === "uploading") && (
                    <Tag color="blue" icon={<LoadingOutlined />}>{charAssetStatus === "uploading" ? "Uploading" : "Processing"}</Tag>
                  )}
                  {charAssetStatus === "failed" && <Tag color="red" icon={<CloseCircleOutlined />}>Failed</Tag>}
                </Space>
                <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                  Upload a portrait to lock the same face across all clips.
                </Text>
                <Space align="start">
                  <Upload accept="image/*" beforeUpload={handleCharRefSelect} showUploadList={false} maxCount={1}>
                    {charRefPreview ? (
                      <img src={charRefPreview} alt="char" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 8, border: "2px solid #1677ff", cursor: "pointer" }} />
                    ) : (
                      <Button icon={<UserOutlined />} size="small">Upload portrait</Button>
                    )}
                  </Upload>
                  {charRefFile && charAssetStatus !== "active" && (
                    <Button size="small" onClick={handleRegisterCharAsset} loading={charAssetStatus === "uploading" || charAssetStatus === "processing"}>
                      Register
                    </Button>
                  )}
                </Space>
              </div>

              <Divider style={{ margin: "4px 0" }} />

              <Space wrap>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleGenerate}
                  disabled={
                    videoRunning ||
                    (videoProvider === "seedance" && (!firstFrameImage || !keyIsSet)) ||
                    (videoProvider === "kling" && !klingKeyIsSet)
                  }
                  loading={videoRunning}
                >
                  Generate {videoClipCount} Clip{videoClipCount !== 1 ? "s" : ""}
                </Button>
                <PromptEditorButton
                  onClick={() => setVideoPromptEditorOpen(true)}
                  hasOverride={Boolean(videoPromptOverrides.main)}
                />
                {videoRunning && <Button danger icon={<StopOutlined />} onClick={handleCancel}>Cancel</Button>}
                {videoClips.length > 0 && !videoRunning && <Button icon={<ReloadOutlined />} onClick={handleReset}>Clear</Button>}
              </Space>

              {videoRunning && videoProgress.total > 0 && (
                <Progress
                  percent={Math.round((videoProgress.done / videoProgress.total) * 100)}
                  status="active"
                  format={() => `${videoProgress.done} / ${videoProgress.total}`}
                />
              )}
            </Space>
          </Col>

          {/* Clips output */}
          <Col xs={24} lg={14}>
            {videoClips.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 120 }}>
                <Space direction="vertical" align="center">
                  <VideoCameraOutlined style={{ fontSize: 32, opacity: 0.15 }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {firstFrameImage ? "Hit Generate to animate your first frame." : "Set a first frame above, then generate."}
                  </Text>
                  {videoProvider === "seedance" && !keyIsSet && <Alert type="warning" showIcon message="Enter your AtlasCloud API key above." style={{ marginTop: 8 }} />}
                  {videoProvider === "kling" && !klingKeyIsSet && <Alert type="warning" showIcon message="Enter your Kling API key above." style={{ marginTop: 8 }} />}
                </Space>
              </div>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {videoClips.map((clip) => (
                  <Card
                    key={clip.index}
                    className="panel-card"
                    size="small"
                    title={
                      <Space>
                        <Text style={{ fontSize: 13 }}>Clip {clip.index + 1}</Text>
                        {clip.status === "generating" && <Tag color="blue" icon={<LoadingOutlined />}>Generating</Tag>}
                        {clip.status === "done" && <Tag color="green" icon={<CheckCircleOutlined />}>Ready</Tag>}
                        {clip.status === "error" && <Tag color="red" icon={<CloseCircleOutlined />}>Failed</Tag>}
                        {clip.status === "cancelled" && <Tag>Cancelled</Tag>}
                      </Space>
                    }
                  >
                    {clip.status === "done" && clip.videoUrl ? (
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <video src={clip.videoUrl} controls style={{ width: "100%", maxHeight: 340, borderRadius: 8, background: "#000" }} />
                        <Space size={6} wrap>
                          <Button
                            size="small"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => triggerDownload(
                              clip.videoUrl,
                              `v${clip.index + 1}-${slugifyPrompt(clip.prompt)}.mp4`,
                            )}
                          >
                            Download
                          </Button>
                          <Button
                            size="small"
                            onClick={() => window.open(clip.videoUrl, "_blank", "noopener")}
                          >
                            Open
                          </Button>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            v{clip.index + 1}-{slugifyPrompt(clip.prompt)}.mp4
                          </Text>
                        </Space>
                      </Space>
                    ) : clip.status === "generating" ? (
                      <Space direction="vertical" size={4} style={{ padding: "12px 0", width: "100%" }}>
                        <Space>
                          <Spin />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {clip.remoteStatus ? `AtlasCloud: ${clip.remoteStatus}` : "Submitting to Seedance 2.0…"}
                          </Text>
                          {clip.elapsedSec > 0 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              ({Math.floor(clip.elapsedSec / 60)}:{String(clip.elapsedSec % 60).padStart(2, "0")} elapsed)
                            </Text>
                          )}
                        </Space>
                        {clip.remoteStatus && (
                          <Text type="secondary" style={{ fontSize: 11 }}>Video generation typically takes 1–3 minutes.</Text>
                        )}
                      </Space>
                    ) : (
                      <Text type={clip.status === "cancelled" ? "secondary" : "danger"} style={{ fontSize: 12 }}>
                        {clip.error || clip.status}
                      </Text>
                    )}
                  </Card>
                ))}
              </Space>
            )}
          </Col>
        </Row>
      </Card>

      {/* Image prompt editor */}
      <PromptEditorModal
        open={imgPromptEditorOpen}
        onClose={() => setImgPromptEditorOpen(false)}
        title="Image Generation Prompts — Video Studio"
        prompts={[{
          id: "main",
          label: "Image Prompt",
          value: buildImgFullPrompt(),
          description: "The full prompt sent to the image model. Includes your text, selected product context, and quality instructions.",
        }]}
        overrides={imgPromptOverrides}
        onSave={(saved) => setImgPromptOverrides(saved)}
      />

      {/* Video prompt editor */}
      <PromptEditorModal
        open={videoPromptEditorOpen}
        onClose={() => setVideoPromptEditorOpen(false)}
        title="Video Motion Prompts — Video Studio"
        prompts={[{
          id: "main",
          label: "Motion Prompt",
          value: videoConcept.trim() || "The scene comes alive with gentle motion and cinematic lighting",
          description: "The motion prompt sent to Seedance for video generation. Describes how the scene should animate.",
        }]}
        overrides={videoPromptOverrides}
        onSave={(saved) => setVideoPromptOverrides(saved)}
      />
    </div>
  );
};

export default VideoStudioSection;
